import { useContext, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { format } from 'date-fns';
import * as R from 'ramda';

import appointmentsWarningIcon from '@atom/components/common/svgIcons/appointmentsWarningIcon.svg';
import UserThumbnail from '@atom/components/common/UserThumbnail';
import AddTeamModalContext from '@atom/components/common/workOrderDetail/addTeamModal/AddTeamModalContext';
import { GET_APPOINTMENT_CONFLICTS } from '@atom/graph/appointments';
import { Icon, Tooltip } from '@atom/mui';
import { CloseIcon } from '@atom/mui/icons';
import { UserDetail } from '@atom/types/user';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import classes from './addTeamModal.module.css';

interface Props {
  user: UserDetail;
}

const UserCartTile = ({ user }: Props) => {
  const { removeUserFromCart, workOrder } = useContext(AddTeamModalContext);

  const [getConflicts, { data: conflictData }] = useLazyQuery(
    GET_APPOINTMENT_CONFLICTS,
    {
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    if (workOrder?.appointmentsEnabled && !isNilOrEmpty(user)) {
      getConflicts({
        variables: {
          input: {
            workOrderId: workOrder?.id,
            plannedStartDate: workOrder?.plannedStartDate,
            dueDate: workOrder?.dueDate,
            userIds: [user?.id],
          },
        },
      });
    }
  }, [user]);

  const name = `${user.firstName} ${user.lastName}`;

  const getWorkConflictName = workDetail => {
    const plannedStartDate = format(
      new Date(workDetail?.plannedStartDate),
      'MM/dd/yyyy hh:mm a',
    );
    const dueDateTime = format(new Date(workDetail?.dueDate), 'hh:mm a');

    return `${plannedStartDate} - ${dueDateTime} ${workDetail?.name}`;
  };

  const appointmentConflicts = R.pathOr(
    [],
    ['appointmentConflicts', 'appointmentConflicts', 0, 'workOrders'],
    conflictData,
  );

  const appointmentConflictContent = () => (
    <div>
      <div>{`This work conflicts with the following work of ${name}:`}</div>
      {appointmentConflicts.map(work => (
        <div>{getWorkConflictName(work)}</div>
      ))}
    </div>
  );

  return (
    <div className={classes.cartTileContainer}>
      <div className={classes.sideTileContainer}>
        <UserThumbnail image={user.photoUrl} alt={name} />
        <div>
          <div className={classes.tileTitle}>{name}</div>
          <div className={classes.tileSubtext}>{user.email}</div>
        </div>
      </div>
      <div>
        {!isNilOrEmpty(appointmentConflicts) && (
          <Tooltip title={appointmentConflictContent()}>
            <span>
              <Icon>
                <img src={appointmentsWarningIcon} />
              </Icon>
            </span>
          </Tooltip>
        )}
        <Icon
          className={classes.closeIcon}
          onClick={() => removeUserFromCart(user.id)}
          icon={CloseIcon}
        />
      </div>
    </div>
  );
};

export default UserCartTile;
